import { useState, useEffect } from "react";
import { format } from "date-fns";
import { isCapabilityEnabled } from "../../config/capabilities";
import {
  updateDoctorFields,
  deleteDoctor,
  updatePassword,
  deactivateUser,
  reactivateUser,
} from "../../utils/functions";
import FirstDeleteModal from "./FirstDeleteModal";
import SecondDeleteModal from "./SecondDeleteModal";
import UpdatePasswordModal from "./UpdatePasswordModal";
import ReactivateAccountModal from "./ReactivateAccountModal";

// Move these components OUTSIDE of the main component to prevent re-creation
const InputField = ({ label, field, type = "text", options = null, placeholder = "", value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    {options ? (
      <select
        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
        value={value || ''}
        onChange={(e) => onChange(e, field)}
      >
        {options.map((option, i) => (
          <option key={i} value={option.value}>{option.label}</option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
        value={value || ''}
        onChange={(e) => onChange(e, field)}
        placeholder={placeholder}
      />
    )}
  </div>
);

const EditableSelectField = ({
  label,
  field,
  options,
  placeholder,
  inputMode,
  setInputMode,
  value,
  onChange
}) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center justify-between">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <button
        type="button"
        onClick={() => setInputMode(inputMode === 'select' ? 'text' : 'select')}
        className="text-xs text-ktx-purple hover:text-purple-700 underline"
      >
        {inputMode === 'select' ? 'Custom' : 'Select'}
      </button>
    </div>
    {inputMode === 'select' ? (
      <select
        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
        value={value || ''}
        onChange={(e) => onChange(e, field)}
      >
        <option value="">{`Select ${label}`}</option>
        {options.map((option, i) => (
          <option key={i} value={option.value}>{option.label}</option>
        ))}
      </select>
    ) : (
      <input
        type="text"
        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
        value={value || ''}
        onChange={(e) => onChange(e, field)}
        placeholder={placeholder}
      />
    )}
  </div>
);

export default function DoctorData({ doctorData, specialties, resetDocForm, orgs, setDoctorData, accountDeleted, setAccountDeleted }) {
  const username = localStorage.getItem('username');
  const userEmail = localStorage.getItem('userEmail');
  const currentDatabase = localStorage.getItem('currentDatabase');
  const canDeactivateVaUsers = isCapabilityEnabled("vaDeactivateUsers");

  const [dynamicDoctorData, setDynamicDoctorData] = useState(doctorData);
  const [changesSaved, setChangesSaved] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState('basic');
  const [specialtyInputMode, setSpecialtyInputMode] = useState('select');
  const [organizationInputMode, setOrganizationInputMode] = useState('select');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [accountDeactivated, setAccountDeactivated] = useState(false);
  const [deactivationError, setDeactivationError] = useState("");
  const [openReactivateModal, setOpenReactivateModal] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [accountReactivated, setAccountReactivated] = useState(false);
  const [reactivationError, setReactivationError] = useState("");
  const isDeactivated =
    dynamicDoctorData?.super_org === "VA-Deactivated" ||
    dynamicDoctorData?.organization === "decommissioned";

  useEffect(() => {
    setDynamicDoctorData(doctorData);
  }, [doctorData]);

  const handleInputChange = (e, field) => {
    let value = e.target.value;

    if (field === 'is_subscribed') {
      value = value === 'true' ? true : value === 'false' ? false : null;
    }

    setDynamicDoctorData(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const handleResetDoctorData = () => {
    setDynamicDoctorData(doctorData);
  };

  const handleCommitDoctorData = async () => {
    dynamicDoctorData.username = username;

    try {
      setDoctorData(await updateDoctorFields(dynamicDoctorData, currentDatabase));
      delete dynamicDoctorData.username;

      setChangesSaved(true);
      setTimeout(() => setChangesSaved(false), 1500);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenPasswordModal = () => setOpenPasswordModal(true);
  const handleClosePasswordModal = () => setOpenPasswordModal(false);
  const handleOpenFirstDeleteModal = () => setOpenDeleteModal('first');
  const handleOpenSecondDeleteModal = () => setOpenDeleteModal('second');
  const handleCloseDeleteModal = () => setOpenDeleteModal("");

  const handleDeactivateUser = async () => {
    setIsDeactivating(true);
    setDeactivationError("");

    try {
      const result = await deactivateUser(
        { ...dynamicDoctorData, username },
        format(new Date(), "yyyy-MM-dd")
      );
      const updatedDoctor = result.user || {
        ...dynamicDoctorData,
        super_org: "VA-Deactivated",
        organization: "decommissioned",
        subscription_end_date: format(new Date(), "yyyy-MM-dd"),
        ehr_password: dynamicDoctorData.npi,
        npi: null,
      };
      setDynamicDoctorData(updatedDoctor);
      setDoctorData(updatedDoctor);
      setAccountDeactivated(true);
      setTimeout(() => setAccountDeactivated(false), 3000);
    } catch (err) {
      setDeactivationError(err.message || "Failed to deactivate account");
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivateUser = async (station) => {
    setIsReactivating(true);
    setReactivationError("");

    try {
      const result = await reactivateUser(dynamicDoctorData, station);
      setDynamicDoctorData(result.user);
      setDoctorData(result.user);
      setOpenReactivateModal(false);
      setAccountReactivated(true);
      setTimeout(() => setAccountReactivated(false), 3000);
    } catch (err) {
      setReactivationError(err.message || "Failed to reactivate account");
    } finally {
      setIsReactivating(false);
    }
  };

  const handleDeleteUser = async () => {
    await deleteDoctor(doctorData.user_id, username, currentDatabase);
    resetDocForm();
    setOpenDeleteModal("");
    setAccountDeleted(true);
    setTimeout(() => setAccountDeleted(false), 1500);
  };

  const handleSubmitPassword = async () => {
    dynamicDoctorData.username = username;
    dynamicDoctorData.password = password;
    dynamicDoctorData.userEmail = userEmail;

    try {
      await updatePassword({ ...dynamicDoctorData }, currentDatabase);
      setPasswordChanged(true);
      setPassword("");
      delete dynamicDoctorData.username;
      delete dynamicDoctorData.password;
      delete dynamicDoctorData.userEmail;
      setOpenPasswordModal(false);
      setTimeout(() => setPasswordChanged(false), 1500);
    } catch (err) {
      console.error(err);
    }
  };

  const getSubscriptionValue = (value) => {
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null) return 'null';
    return '';
  };

  const getTrialEndDate = () => {
    if (!dynamicDoctorData.created_at) return '';

    const createdDate = new Date(dynamicDoctorData.created_at);
    const trialEndDate = new Date(createdDate);
    trialEndDate.setDate(createdDate.getDate() + 7);

    return trialEndDate.toISOString().split('T')[0];
  };

  const handleTrialEndDateChange = (e) => {
    const trialEndDate = e.target.value;

    if (trialEndDate) {
      // Calculate created_at date (7 days before trial end)
      const endDate = new Date(trialEndDate);
      const createdDate = new Date(endDate);
      createdDate.setDate(endDate.getDate() - 7);

      // Format for database (YYYY-MM-DD format)
      const formattedCreatedDate = createdDate.toISOString().split('T')[0];

      setDynamicDoctorData(prevState => ({
        ...prevState,
        created_at: formattedCreatedDate
      }));
    }
  };

  const isTrialUser = () => {
    return dynamicDoctorData.is_subscribed === false || dynamicDoctorData.is_subscribed === null;
  };

  const hasChanges = JSON.stringify(dynamicDoctorData) !== JSON.stringify(doctorData);

  if (!dynamicDoctorData) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple"></span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b">
        <h2 className="text-2xl font-bold text-ktx-purple">{dynamicDoctorData.user_name}</h2>
        <button
          onClick={resetDocForm}
          className="px-4 py-2 border border-sage-green text-sage-green hover:bg-sage-green hover:text-white rounded-md font-medium transition-colors"
        >
          New Doctor
        </button>
      </div>

      {/* Tabs - Equal Width */}
      <div className="grid grid-cols-5 gap-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'basic', label: 'Basic Info' },
          { id: 'professional', label: 'Professional' },
          { id: 'subscription', label: 'Subscription' },
          { id: 'information', label: 'Information' },
          { id: 'actions', label: 'Actions' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-2 py-2 rounded-md font-medium transition-colors text-center ${activeTab === tab.id
              ? 'bg-white text-ktx-purple shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Full Name"
              field="user_name"
              placeholder="Enter full name"
              value={dynamicDoctorData.user_name}
              onChange={handleInputChange}
            />
            <InputField
              label="Email Address"
              field="user_email"
              type="email"
              placeholder="Enter email address"
              value={dynamicDoctorData.user_email}
              onChange={handleInputChange}
            />
            <InputField
              label="Phone Number"
              field="phone_number"
              placeholder="Enter phone number"
              value={dynamicDoctorData.phone_number}
              onChange={handleInputChange}
            />
            <EditableSelectField
              label="Specialty"
              field="specialty"
              options={specialties.map(s => ({ value: s.specialty, label: s.specialty }))}
              placeholder="Enter custom specialty"
              inputMode={specialtyInputMode}
              setInputMode={setSpecialtyInputMode}
              value={dynamicDoctorData.specialty}
              onChange={handleInputChange}
            />
            <EditableSelectField
              label="Organization"
              field="organization"
              options={orgs.map(o => ({ value: o.organization, label: o.organization }))}
              placeholder="Enter custom organization"
              inputMode={organizationInputMode}
              setInputMode={setOrganizationInputMode}
              value={dynamicDoctorData.organization}
              onChange={handleInputChange}
            />
            <InputField
              label="Super Organization"
              field="super_org"
              placeholder="Enter super organization"
              value={dynamicDoctorData.super_org}
              onChange={handleInputChange}
            />
          </div>
        )}

        {/* Professional Tab */}
        {activeTab === 'professional' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="NPI Number"
              field="npi"
              placeholder="Enter NPI number"
              value={dynamicDoctorData.npi}
              onChange={handleInputChange}
            />
            <InputField
              label="Model Type"
              field="model_type"
              options={[
                { value: '', label: 'Select Model Type' },
                { value: 'Multilingual', label: 'Multilingual' },
                { value: 'English', label: 'English' }
              ]}
              value={dynamicDoctorData.model_type}
              onChange={handleInputChange}
            />
            <InputField
              label="Turnaround Time"
              field="turnaround_time"
              options={[
                { value: '', label: 'Select Turnaround Time' },
                { value: 'Real Time', label: 'Real Time' },
                { value: 'Normal', label: 'Normal' },
              ]}
              value={dynamicDoctorData.turnaround_time}
              onChange={handleInputChange}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">QA Checklist</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
                rows="3"
                value={dynamicDoctorData.qa_checklist || ''}
                onChange={(e) => handleInputChange(e, 'qa_checklist')}
                placeholder="Enter QA checklist items"
              />
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Subscription Status"
              field="is_subscribed"
              options={[
                { value: 'true', label: 'Yes - Subscribed' },
                { value: 'false', label: 'No - Not Subscribed' },
                { value: 'null', label: 'Not yet' }
              ]}
              value={getSubscriptionValue(dynamicDoctorData.is_subscribed)}
              onChange={handleInputChange}
            />
            {isTrialUser() && (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Trial End Date</label>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
                  value={getTrialEndDate()}
                  onChange={handleTrialEndDateChange}
                />
                <span className="text-xs text-gray-500">
                  Account created date will be adjusted to give exactly 7 days trial
                </span>
              </div>
            )}
            <InputField
              label="Subscription ID"
              field="subscription_id"
              placeholder="Enter subscription ID"
              value={dynamicDoctorData.subscription_id}
              onChange={handleInputChange}
            />
            <InputField
              label="Subscription End Date"
              field="subscription_end_date"
              type="date"
              value={dynamicDoctorData.subscription_end_date}
              onChange={handleInputChange}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Access Token</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent font-mono text-sm"
                rows="2"
                value={dynamicDoctorData.access_token || ''}
                onChange={(e) => handleInputChange(e, 'access_token')}
                placeholder="Enter access token"
              />
            </div>
          </div>
        )}

        {/* Information Tab */}
        {activeTab === 'information' && (
          <div className="grid grid-cols-1 gap-6">
            <InputField
              label="EHR Password"
              field="ehr_password"
              type="text"
              placeholder="Enter EHR password"
              value={dynamicDoctorData.ehr_password}
              onChange={handleInputChange}
            />
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-medium text-gray-800 mb-2">Account Information</h4>
              <div className="grid grid-cols-1 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">User ID:</span>
                  <span className="ml-2 font-mono">{dynamicDoctorData.user_id}</span>
                </div>
                <div>
                  <span className="text-gray-600">Created Date:</span>
                  <span className="ml-2">{new Date(dynamicDoctorData.created_at).toLocaleDateString()}</span>
                </div>

                {/* Show trial info for trial users */}
                {isTrialUser() && (
                  <>
                    <div>
                      <span className="text-gray-600">Last Day of Active Trial:</span>
                      <span className="ml-2 text-orange-600 font-medium">
                        {new Date(getTrialEndDate()).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Trial Status:</span>
                      <span className="ml-2">
                        {new Date(getTrialEndDate()) > new Date() ? (
                          <span className="text-green-600">Active Trial</span>
                        ) : (
                          <span className="text-red-600">Trial Expired</span>
                        )}
                      </span>
                    </div>
                  </>
                )}

                <div>
                  <span className="text-gray-600">QA Notes:</span>
                  <span className="ml-2">{dynamicDoctorData.qa}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <h4 className="font-medium text-yellow-800 mb-2">Password Management</h4>
              <p className="text-yellow-700 text-sm mb-3">Reset the user's password. They will receive an email with login instructions.</p>
              <button
                onClick={handleOpenPasswordModal}
                className="px-4 py-2 border-2 border-ktx-purple text-ktx-purple hover:bg-ktx-purple hover:text-white rounded-md font-medium transition-colors"
              >
                Reset Password
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <h4 className="font-medium text-red-800 mb-2">Danger Zone</h4>
              <p className="text-red-700 text-sm mb-3">Permanently delete this user account. This action cannot be undone.</p>
              <button
                onClick={handleOpenFirstDeleteModal}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors"
              >
                Delete Account
              </button>
            </div>

            {canDeactivateVaUsers && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="font-medium text-blue-800 mb-2">
                  {isDeactivated ? "VA Reactivation" : "VA Deactivation"}
                </h4>
                <p className="text-blue-700 text-sm mb-3">
                  {isDeactivated
                    ? "Reactivate this account by assigning a supported VA station. The provider organization and NPI will be restored."
                    : 'This deactivates the account, changes the VA super organization to "VA-Deactivated", and marks the organization as decommissioned.'}
                </p>
                {isDeactivated ? (
                  <button
                    type="button"
                    onClick={() => {
                      setReactivationError("");
                      setOpenReactivateModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                  >
                    Reactivate Account
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!dynamicDoctorData.npi || isDeactivating}
                    onClick={handleDeactivateUser}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeactivating ? "Deactivating…" : "Deactivate Account"}
                  </button>
                )}
                {!isDeactivated && deactivationError && (
                  <p className="mt-3 text-sm text-red-600">
                    {deactivationError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-center gap-4 mt-8 pt-6 border-t">
          <button
            onClick={handleResetDoctorData}
            className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors min-w-[150px]"
          >
            Revert Changes
          </button>
          <button
            onClick={handleCommitDoctorData}
            className="px-6 py-3 bg-sage-green hover:bg-teal-700 text-white rounded-md font-medium transition-colors min-w-[150px]"
          >
            Save Changes
          </button>
        </div>
      )}

      {/* Status Messages */}
      <div className="mt-4 text-center">
        {changesSaved && <p className="text-green-600 font-medium">✅ Changes saved successfully!</p>}
        {accountDeactivated && <p className="text-blue-600 font-medium">Account deactivated successfully.</p>}
        {accountReactivated && <p className="text-green-600 font-medium">Account reactivated successfully.</p>}
        {accountDeleted && <p className="text-red-600 font-medium">🗑️ Account deleted!</p>}
        {passwordChanged && <p className="text-green-600 font-medium">🔑 Password updated!</p>}
      </div>

      {/* Modals */}
      {openDeleteModal === 'first' && (
        <FirstDeleteModal
          handleCloseDeleteModal={handleCloseDeleteModal}
          handleOpenSecondDeleteModal={handleOpenSecondDeleteModal}
        />
      )}
      {openDeleteModal === 'second' && (
        <SecondDeleteModal
          doctor_id={doctorData.user_id}
          handleDeleteUser={handleDeleteUser}
          handleCloseDeleteModal={handleCloseDeleteModal}
        />
      )}
      {openPasswordModal && (
        <UpdatePasswordModal
          setPassword={setPassword}
          password={password}
          handleClosePasswordModal={handleClosePasswordModal}
          handleSubmitPassword={handleSubmitPassword}
        />
      )}
      {openReactivateModal && (
        <ReactivateAccountModal
          doctorName={dynamicDoctorData.user_name}
          error={reactivationError}
          isSubmitting={isReactivating}
          onClose={() => {
            if (!isReactivating) {
              setOpenReactivateModal(false);
              setReactivationError("");
            }
          }}
          onSubmit={handleReactivateUser}
        />
      )}
    </div>
  );
}